import { clsx } from 'clsx';
import { Flex, type FlexProps } from '@repo/ui/Flex';
import { Badge } from '@repo/ui/Badge';
import { clientLogger } from '../../utils/logger';
import s from './ItemCard.module.css';
import {
  type MouseEventHandler,
  type ReactNode,
  type ComponentProps,
  useState,
} from 'react';
import type { OrderItem, Claim } from '../../graphql/query/getOrder';
import type { DisplayItem } from '../../utils/expandItemsWithClaims';
import { Dropdown } from '../Dropdown/Dropdown';
import { Text, type LanguageCore, rawString } from '../Language/Language';
import { Icon } from '@repo/ui/Icon';
import { IconButton } from '@repo/ui/IconButton';
import { Callout } from '../Callout/Callout';
import { type Control, Controller } from 'react-hook-form';
import type { WorkflowData } from '../../workflow';
import { separatePriceFromAttributes } from '../../utils/itemAttributes';
import { getItemDisplayConfig } from '../../utils/itemSelectConfig';
import type { ItemSelectConfig } from '../../graphql/query/getRetailerSettings';

export type StatusBadgeVariant = 'ineligible' | 'override';

export type StatusBadge = {
  label: string;
  variant: StatusBadgeVariant;
};

type ImageSize = 'sm' | 'md' | 'lg' | 'xl';

type ItemCardOrderItem = Omit<OrderItem, 'quantity'> & {
  quantity?: OrderItem['quantity'];
};

export type ItemCardViewProps = {
  className?: string;
  containerClassName?: string;
  children?: ReactNode;
  hideImage?: boolean;
  onExpand?: () => void;
  onRemove?: () => void;
  item?: ItemCardOrderItem;
  imageSize?: ImageSize;
  image?: string;
  name?: string;
  itemId?: string;
  onQuantityChange?: ComponentProps<'select'>['onChange'];
  additionalDetails?: Detail[];
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  borderRadius?: 'sm' | 'md' | 'lg' | 'xl';
  'data-testid'?: string;
  // Passed from logic container
  remainingQuantity?: number;
  isShipmentScopeReason?: boolean;
  control?: Control<WorkflowData>;
  itemSelectConfig?: ItemSelectConfig | null;
  /** Optional message to display when item is ineligible for a claim */
  ineligibilityMessage?: LanguageCore | null;
  /** Claims associated with this item */
  claims?: Claim[] | null;
  /** Callback when a claim status is clicked */
  onClaimClick?: (claimId: string) => void;
  /** Optional status badges to display above the title */
  statusBadges?: StatusBadge[] | null;
} & Omit<FlexProps, 'children'>;

const ItemImage = ({
  imageSize = 'md',
  image,
  name,
}: {
  imageSize: ImageSize;
  image: string;
  name: string;
}) => (
  <div className={clsx([s.imageWrap, s[imageSize]])}>
    {image ? (
      <img
        alt={name}
        className={clsx([s.image])}
        src={image}
        loading="lazy"
        onError={() => {
          clientLogger.logError(
            {
              message: 'Image failed to load',
            },
            'Image failed to load',
          );
          // TODO: Add image fallback
        }}
      />
    ) : (
      <div className={s.imageFallback} aria-hidden="true" />
    )}
  </div>
);

const Price = ({ price }: { price?: string }) => (
  <div className={s.price}>{price}</div>
);

export type Detail = {
  name: string;
  value: string;
};

/**
 * Get the display label for an attribute from translations.
 * Falls back to capitalized attribute name if no translation exists.
 */
const getAttributeLabel = (name: string): string => {
  const langKey = `Global.item.attribute.${name.toLowerCase()}`;
  const translated = rawString({ langKey });
  if (translated && translated !== langKey) {
    return translated;
  }
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const Details = ({ details }: { details: Detail[] }) => (
  <dl className={s.details}>
    {details.map(({ name, value }) => {
      const label = getAttributeLabel(name);
      return (
        <div key={`${name}-${value}`} className={s.detailsLine}>
          <dt className={s.detailsLabel}>{`${label}:`}</dt>
          <dd className={s.detailsValue}>{value}</dd>
        </div>
      );
    })}
  </dl>
);

const Title = ({ id, name }: { id: string; name: string }) => (
  <h3 id={id} className={s.title} data-testid="itemTitle">
    {name}
  </h3>
);

const badgeTypeMap: Record<StatusBadgeVariant, 'negative' | 'positive'> = {
  ineligible: 'negative',
  override: 'positive',
};

const StatusBadges = ({ badges }: { badges: StatusBadge[] }) => {
  if (!badges || badges.length === 0) return null;

  return (
    <Flex gap="g-8" wrap="wrap" className={s.statusBadges}>
      {badges.map((badge, index) => (
        <Badge
          key={`${badge.variant}-${index}`}
          type={badgeTypeMap[badge.variant]}
          tone="subtle"
          size="sm"
        >
          {badge.label}
        </Badge>
      ))}
    </Flex>
  );
};

const Actions = ({
  onRemove,
  itemId,
  collapsible,
  isCollapsed,
  onToggleCollapse,
}: {
  onRemove?: () => void;
  itemId?: string;
  collapsible?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}) => (
  <div className={s.actions}>
    {onRemove && (
      <IconButton
        data-item-id={itemId}
        icon="delete"
        label={<Text langKey="Global.itemSelection.remove" />}
        onClick={onRemove}
        size="lg"
        variant="tertiary"
      />
    )}
    {collapsible && onToggleCollapse && (
      <IconButton
        className={s.collapseButton}
        data-item-id={itemId}
        data-collapsed={isCollapsed}
        icon="chevron-up"
        aria-expanded={!isCollapsed}
        label={
          <Text langKey={isCollapsed ? 'Global.expand' : 'Global.collapse'} />
        }
        onClick={onToggleCollapse}
        size="lg"
        variant="tertiary"
      />
    )}
  </div>
);

const IneligibilityBanner = ({ message }: { message: LanguageCore }) => (
  <div className={s.ineligibilityBanner}>
    <Callout variant="info">
      <Text {...message} />
    </Callout>
  </div>
);

const ClaimStatusItem = ({
  claim,
  onClaimClick,
}: {
  claim: Claim;
  onClaimClick?: (claimId: string) => void;
}) => {
  const reasonText = claim.reasonId
    ? rawString({ langKey: `Global.reason.${claim.reasonId}.title` })
    : null;
  const hasReasonText = reasonText && reasonText.trim().length > 0;

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      gap="g-8"
      className={s.claimStatusFooter}
    >
      {hasReasonText && <span className={s.claimReason}>{reasonText}</span>}
      {onClaimClick ? (
        <button
          type="button"
          className={s.claimStatusButton}
          onClick={() => onClaimClick(claim.claimId)}
        >
          <Badge type="neutral" tone="subtle" size="sm">
            <Text langKey={`Global.claimStatus.${claim.status}`} />
          </Badge>
          <Icon name="chevron-right" size="sm" />
        </button>
      ) : (
        <span className={s.claimStatusBadgeWrapper}>
          <Badge type="neutral" tone="subtle" size="sm">
            <Text langKey={`Global.claimStatus.${claim.status}`} />
          </Badge>
        </span>
      )}
    </Flex>
  );
};

const ClaimStatusBanner = ({
  claims,
  onClaimClick,
}: {
  claims: Claim[];
  onClaimClick?: (claimId: string) => void;
}) => {
  if (!claims || claims.length === 0) return null;

  return (
    <Flex direction="column" gap="g-2" className={s.claimStatusBanner}>
      {claims.map((claim) => (
        <ClaimStatusItem
          key={claim.claimItemId}
          claim={claim}
          onClaimClick={onClaimClick}
        />
      ))}
    </Flex>
  );
};

const QuantitySelector = ({
  itemId,
  onQuantityChange,
  remainingQuantity,
  disabled = false,
  control,
  value,
}: {
  itemId?: string;
  onQuantityChange?: ComponentProps<'select'>['onChange'];
  remainingQuantity: number;
  disabled?: boolean;
  control?: Control<WorkflowData>;
  value?: number;
}) => {
  const options = [...Array(remainingQuantity)].map((_, idx) => {
    const val = idx + 1;
    return {
      value: val.toString(),
      label: val.toString(),
    };
  });

  const fieldName = `items.${itemId}.itemSelection.quantity` as const;

  if (!control || !itemId) {
    return (
      <div data-testid="quantitySelector">
        <Dropdown
          options={options}
          disabled={disabled}
          value={(value || 1).toString()}
          onChange={(e) => {
            const numValue = parseInt(e.target.value, 10);
            if (onQuantityChange) {
              onQuantityChange({
                ...e,
                target: { ...e.target, value: numValue.toString() },
              } as unknown as React.ChangeEvent<HTMLSelectElement>);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div data-testid="quantitySelector">
      <Controller
        name={fieldName}
        control={control}
        render={({ field }) => (
          <Dropdown
            options={options}
            disabled={disabled}
            value={field.value?.toString() || '1'}
            onValueChange={(val) => {
              const numValue = parseInt(val, 10);
              field.onChange(numValue);
              if (onQuantityChange) {
                onQuantityChange({
                  target: { value: numValue.toString(), name: field.name },
                } as unknown as React.ChangeEvent<HTMLSelectElement>);
              }
            }}
            onBlur={field.onBlur}
            name={field.name}
          />
        )}
      />
    </div>
  );
};

/**
 * Pure presentational component for item content.
 * No hooks - all data passed via props.
 */
const ItemContentView = ({
  item,
  itemId,
  onQuantityChange,
  onRemove,
  collapsible,
  isCollapsed,
  onToggleCollapse,
  additionalDetails,
  remainingQuantity = 1,
  isShipmentScopeReason = false,
  control,
  itemSelectConfig,
}: {
  item: ItemCardOrderItem;
  itemId?: string;
  onQuantityChange?: ComponentProps<'select'>['onChange'];
  onRemove?: () => void;
  collapsible?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  additionalDetails?: Detail[];
  remainingQuantity?: number;
  isShipmentScopeReason?: boolean;
  control?: Control<WorkflowData>;
  itemSelectConfig?: ItemSelectConfig | null;
}) => {
  const showQuantitySelector =
    itemId && onQuantityChange && item.quantity && remainingQuantity > 1;

  // Separate price from other attributes
  const itemInfo = separatePriceFromAttributes(item?.variant?.info);

  // Get display configuration (filtered details and price visibility)
  const { details: filteredDetails, showPrice } = getItemDisplayConfig(
    itemInfo,
    itemSelectConfig,
  );

  const details: Detail[] = [...filteredDetails, ...(additionalDetails ?? [])];

  return (
    <>
      <Title id={`${item.id}-title`} name={item.name} />
      {details.length > 0 && <Details details={details} />}
      {showPrice && itemInfo?.price && <Price price={itemInfo.price} />}
      {showQuantitySelector && (
        <QuantitySelector
          itemId={itemId}
          onQuantityChange={onQuantityChange}
          remainingQuantity={remainingQuantity}
          disabled={isShipmentScopeReason}
          control={control}
        />
      )}

      <Actions
        onRemove={onRemove}
        itemId={itemId}
        collapsible={collapsible}
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
      />
    </>
  );
};

const borderRadiusVariants = {
  sm: s.wrapperSm,
  md: s.wrapperMd,
  lg: s.wrapperLg,
  xl: s.wrapperXl,
} as const;

/**
 * Pure presentational ItemCard component.
 * Contains no hooks (except useState for local collapse state).
 * All workflow/form data passed via props.
 */
export const ItemCardView = ({
  className = '',
  containerClassName = '',
  imageSize = 'md',
  item,
  image,
  name,
  children,
  itemId,
  onQuantityChange,
  additionalDetails,
  onRemove,
  collapsible = false,
  defaultCollapsed = false,
  borderRadius = 'md',
  'data-testid': dataTestId,
  remainingQuantity,
  isShipmentScopeReason,
  control,
  itemSelectConfig,
  ineligibilityMessage,
  claims,
  onClaimClick,
  statusBadges,
  ...rest
}: ItemCardViewProps) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const hasBadges = statusBadges && statusBadges.length > 0;

  return (
    <Flex
      className={clsx(
        s.wrapper,
        borderRadiusVariants[borderRadius],
        containerClassName,
      )}
      direction="column"
      data-testid={dataTestId ?? 'itemCard'}
      {...rest}
    >
      <Flex
        className={clsx(s.header, className)}
        data-has-badges={hasBadges ? 'true' : undefined}
      >
        <ItemImage
          image={image || item?.image?.url || ''}
          imageSize={imageSize}
          name={name || item?.name || ''}
        />
        {hasBadges && <StatusBadges badges={statusBadges} />}
        {item && (
          <ItemContentView
            item={item}
            itemId={itemId}
            onQuantityChange={onQuantityChange}
            additionalDetails={additionalDetails}
            onRemove={onRemove}
            collapsible={collapsible}
            isCollapsed={isCollapsed}
            onToggleCollapse={handleToggleCollapse}
            remainingQuantity={remainingQuantity}
            isShipmentScopeReason={isShipmentScopeReason}
            control={control}
            itemSelectConfig={itemSelectConfig}
          />
        )}
      </Flex>
      {children && !isCollapsed && <div className={s.content}>{children}</div>}
      {claims && claims.length > 0 && (
        <ClaimStatusBanner claims={claims} onClaimClick={onClaimClick} />
      )}
      {ineligibilityMessage && (
        <IneligibilityBanner message={ineligibilityMessage} />
      )}
    </Flex>
  );
};

/**
 * Pure presentational ItemCardButton component.
 * Ensures ARIA/interactive props are on the actual <Button> element.
 */
export const ItemCardButtonView = (
  props: ItemCardViewProps & {
    onClick: MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
  },
) => {
  const {
    onClick,
    disabled,
    'aria-labelledby': ariaLabelledby,
    'aria-label': ariaLabel,
    id,
    ...viewProps
  } = props;

  return (
    <button
      type="button"
      id={id}
      aria-labelledby={ariaLabelledby}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={clsx(s.button, disabled && s.disabled)}
    >
      <ItemCardView {...viewProps} />
    </button>
  );
};

/**
 * Renders a claimed item card with claim status.
 * When onClaimClick is provided, the entire card is clickable.
 */
export const ClaimedItemCard = ({
  displayItem,
  imageSize,
  itemSelectConfig,
  onClaimClick,
  ariaLabelledby,
}: {
  displayItem: DisplayItem;
  imageSize?: 'sm' | 'md' | 'lg' | 'xl';
  itemSelectConfig?: ItemCardViewProps['itemSelectConfig'];
  onClaimClick?: (claimId: string) => void;
  ariaLabelledby?: string;
}) => {
  if (!displayItem.singleClaim) return null;

  // Use the claim quantity (stored in quantity.ordered for claimed items)
  // or fall back to the claim's quantity field, or default to 1
  const claimQuantity =
    displayItem.quantity?.ordered ?? displayItem.singleClaim?.quantity ?? 1;

  const labelledby = ariaLabelledby ?? `${displayItem.displayId}-title`;

  const itemCardView = (
    <ItemCardView
      aria-labelledby={labelledby}
      imageSize={imageSize}
      item={displayItem}
      remainingQuantity={claimQuantity}
      isShipmentScopeReason={false}
      itemSelectConfig={itemSelectConfig}
      claims={[displayItem.singleClaim]}
    />
  );

  if (onClaimClick) {
    return (
      <button
        type="button"
        aria-labelledby={labelledby}
        onClick={() => onClaimClick(displayItem.singleClaim!.claimId)}
        className={s.button}
      >
        {itemCardView}
      </button>
    );
  }

  return itemCardView;
};
